/**
 * globalFilterStore — Store reactivo para filtros globales que persisten
 * entre páginas vía localStorage.
 *
 * Cualquier componente puede leer/escribir filtros. Cuando un filtro cambia,
 * se emite un CustomEvent `gf:change` para que las páginas puedan reaccionar
 * (re-fetch, re-filter, etc.).
 *
 * @example
 *   import { useGlobalFilter } from '@/stores/globalFilterStore'
 *
 *   const { state, set, get, clear, onChange } = useGlobalFilter()
 *
 *   // Escribir
 *   set('globalAsesorId', 5)
 *   set('globalAsesorName', 'M. Montoya')
 *
 *   // Leer (reactivo)
 *   state.globalAsesorId  // 5
 *   get('globalAsesorId') // 5
 *
 *   // Suscribirse a cambios
 *   const unsub = onChange(e => {
 *     console.log(e.detail.key, e.detail.value)
 *   })
 *   onUnmounted(unsub)
 *
 *   // Limpiar
 *   clear('globalAsesorId')
 */

import { reactive } from 'vue'

const STORAGE_KEY = 'vybroo_global_filters'

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function save(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
}

// Reactive state — compartido entre todos los consumers del store
const state = reactive(load())

const EVENT_NAME = 'gf:change'

export function useGlobalFilter() {
  /**
   * Establece un valor de filtro, persiste y emite evento de cambio.
   */
  function set(key, value) {
    state[key] = value
    save({ ...state })
    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, { detail: { key, value } })
    )
  }

  /**
   * Establece múltiples valores en batch y emite un SOLO evento de cambio.
   * Útil para evitar race conditions cuando se cambian varios filtros juntos.
   *
   * @param {Record<string, any>} entries - Objeto { key: value, ... }
   * @example setBatch({ globalAsesorId: 5, globalAsesorName: 'M. Montoya' })
   */
  function setBatch(entries) {
    if (!entries || typeof entries !== 'object') return
    const keys = Object.keys(entries)
    if (keys.length === 0) return
    keys.forEach((key) => {
      state[key] = entries[key]
    })
    save({ ...state })
    // Emitir solo con la primera key como referencia
    const firstKey = keys[0]
    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, { detail: { key: firstKey, value: entries[firstKey], batch: true } })
    )
  }

  /**
   * Retorna el valor de un filtro (o null si no existe).
   */
  function get(key) {
    return key in state ? state[key] : null
  }

  /**
   * Elimina un filtro, persiste y emite evento de cambio.
   */
  function clear(key) {
    delete state[key]
    save({ ...state })
    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, { detail: { key, value: null } })
    )
  }

  /**
   * Elimina múltiples filtros en batch y emite un SOLO evento de cambio.
   *
   * @param {string[]} keys - Lista de keys a eliminar
   * @example clearBatch(['globalAsesorId', 'globalAsesorName'])
   */
  function clearBatch(keys) {
    if (!Array.isArray(keys) || keys.length === 0) return
    keys.forEach((key) => {
      delete state[key]
    })
    save({ ...state })
    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, { detail: { key: keys[0], value: null, batch: true } })
    )
  }

  /**
   * Suscribe un callback a cambios en cualquier filtro.
   * Retorna función para desuscribirse (útil en onUnmounted).
   *
   * @param {(e: CustomEvent) => void} callback
   * @returns {() => void}
   */
  function onChange(callback) {
    window.addEventListener(EVENT_NAME, callback)
    return () => window.removeEventListener(EVENT_NAME, callback)
  }

  return { state, set, setBatch, get, clear, clearBatch, onChange }
}
